// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/CandidateForm.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Upload, Loader2 } from 'lucide-react';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import FormOffCanvas from '../ui/FormOffCanvas'; // Imported from helpers folder
import SelectDropdown from '../../customStyles/SelectDropdown'; // Imported custom SelectDropdown

// Static configurations for SelectDropdown options
const sourceOptions = [
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'job_portal', label: 'Job Portal' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'agency', label: 'Agency' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'other', label: 'Other' }
];

const ratingOptions = [
  { value: 1, label: '★ 1 - Poor' },
  { value: 2, label: '★ 2 - Average' },
  { value: 3, label: '★ 3 - Good' },
  { value: 4, label: '★ 4 - Strong' },
  { value: 5, label: '★ 5 - Exceptional' }
];

const CandidateForm = ({ isOpen, onClose, mode = "apply", candidate, jobOpeningId, organizationId, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    totalExperienceYears: 0,
    currentLocation: '',
    currentCompany: '',
    currentDesignation: '',
    currentCtc: '',
    expectedCtc: '',
    noticePeriodDays: '',
    skills: '', // input string, backend converts to array
    source: 'website',
    rating: 3,
    tags: '', // input string, split to array
    isOnHold: false
  });

  // Prefills all data from candidate model when in edit mode
  useEffect(() => {
    if (mode === "edit" && candidate) {
      setFormData({
        firstName: candidate.firstName || '',
        lastName: candidate.lastName || '',
        email: candidate.email || '',
        mobile: candidate.mobile || '',
        totalExperienceYears: candidate.totalExperienceYears || 0,
        currentLocation: candidate.currentLocation || '',
        currentCompany: candidate.currentCompany || '',
        currentDesignation: candidate.currentDesignation || '',
        currentCtc: candidate.currentCtc || '',
        expectedCtc: candidate.expectedCtc || '',
        noticePeriodDays: candidate.noticePeriodDays || '',
        skills: candidate.skills?.join(', ') || '',
        source: candidate.source || 'website',
        rating: candidate.rating || 3,
        tags: candidate.tags?.join(', ') || '',
        isOnHold: candidate.isOnHold || false
      });
    } else if (mode === "apply") {
      setFormData({
        firstName: '', lastName: '', email: '', mobile: '', totalExperienceYears: 0,
        currentLocation: '', currentCompany: '', currentDesignation: '',
        currentCtc: '', expectedCtc: '', noticePeriodDays: '', skills: '', source: 'website',
        rating: 3, tags: '', isOnHold: false
      });
      setResumeFile(null);
    }
  }, [mode, candidate, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setResumeFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (mode === "apply") {
        // Prepare form data structure required for Apply mode
        const data = new FormData();
        data.append("organizationId", organizationId || "651a2b3c4d5e6f7a8b9c0d11");
        data.append("jobOpeningId", jobOpeningId);
        data.append("firstName", formData.firstName);
        data.append("lastName", formData.lastName);
        data.append("email", formData.email);
        data.append("mobile", formData.mobile);
        data.append("totalExperienceYears", formData.totalExperienceYears);
        data.append("currentLocation", formData.currentLocation);
        data.append("skills", formData.skills);
        data.append("source", formData.source);
        if (resumeFile) {
          data.append("resume", resumeFile); // Append resume file upload
        }

        const response = await api.post(SummaryApi.careerApply.url, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          toast.success("Application submitted successfully!");
          if (onSave) onSave();
          onClose();
        }
      } else {
        // Prepare JSON payload configuration required for Edit mode
        const processedData = {
          ...formData,
          skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        };
        const response = await api.put(SummaryApi.updateCandidates.url(candidate._id), processedData);
        if (response.data.success) {
          toast.success("Profile details updated successfully");
          if (onSave) onSave();
          onClose();
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormOffCanvas isOpen={isOpen} onClose={onClose} title={mode === "apply" ? "Apply for Job opening" : "Edit Candidate Details"}>
      <form onSubmit={handleSubmit} className="space-y-5">
        
        {/* SECTION 1: Personal details (For both Apply and Edit mode) */}
        <div className="space-y-3">
          <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Contact Credentials</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-gray-600 mb-1">First Name *</label>
              <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-gray-600 mb-1">Last Name</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-gray-600 mb-1">Email Address *</label>
              <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-gray-600 mb-1">Mobile No *</label>
              <input type="tel" name="mobile" required value={formData.mobile} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-gray-600 mb-1">Total Exp (Yrs)</label>
              <input type="number" name="totalExperienceYears" min="0" value={formData.totalExperienceYears} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-gray-600 mb-1">Current Location</label>
              <input type="text" name="currentLocation" value={formData.currentLocation} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" placeholder="e.g. Chennai" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-gray-600 mb-1">Skills (Comma-separated)</label>
            <input type="text" name="skills" value={formData.skills} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" placeholder="React, NodeJS, Express" />
          </div>
        </div>

        {/* SECTION 2: Recruiter Internal Details (Only displayed in EDIT mode) */}
        {mode === "edit" && (
          <div className="space-y-4 border-t pt-4">
            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Employment & CTC Config</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-gray-600 mb-1">Current Company</label>
                <input type="text" name="currentCompany" value={formData.currentCompany} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-600 mb-1">Current Designation</label>
                <input type="text" name="currentDesignation" value={formData.currentDesignation} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[11px] font-bold text-gray-600 mb-1">Current CTC</label>
                <input type="number" name="currentCtc" value={formData.currentCtc} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-600 mb-1">Expected CTC</label>
                <input type="number" name="expectedCtc" value={formData.expectedCtc} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-600 mb-1">Notice (Days)</label>
                <input type="number" name="noticePeriodDays" min="0" value={formData.noticePeriodDays} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Internal Sourcing Config</h4>
              <div className="grid grid-cols-2 gap-3">
                
                {/* Application Source SelectDropdown */}
                <div>
                  <SelectDropdown
                    label="Application Source"
                    options={sourceOptions}
                    value={formData.source}
                    onChange={(val) => setFormData(prev => ({ ...prev, source: val }))}
                    valueKey="value"
                    labelKey="label"
                    searchable={false}
                    zIndex={66}
                    multiple={false}
                  />
                </div>

                {/* Profile Rating SelectDropdown */}
                <div>
                  <SelectDropdown
                    label="Profile Rating"
                    options={ratingOptions}
                    value={formData.rating}
                    onChange={(val) => setFormData(prev => ({ ...prev, rating: Number(val) }))}
                    valueKey="value"
                    labelKey="label"
                    searchable={false}
                    zIndex={66}
                    multiple={false}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-gray-600 mb-1">Candidate Tags (Comma-separated)</label>
                <input type="text" name="tags" value={formData.tags} onChange={handleChange} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" placeholder="Fast-learner, Remote, Immediate" />
              </div>

              {/* Hold Config Checkbox */}
              <div className="flex items-center gap-2 pt-2">
                <input type="checkbox" name="isOnHold" id="isOnHold" checked={formData.isOnHold} onChange={handleChange} className="h-4 w-4 accent-red-600 cursor-pointer rounded" />
                <label htmlFor="isOnHold" className="text-xs font-bold text-gray-700 cursor-pointer">Place Candidate on Hold</label>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 3: Resume upload (Only required in APPLY mode) */}
        {mode === "apply" && (
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Attach Profile Resume *</h4>
            <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-red-50/20 hover:border-primary transition-all">
              <Upload className="h-5 w-5 text-gray-400 mb-1" />
              <span className="text-[10px] text-gray-500 font-semibold">{resumeFile ? resumeFile.name : "Upload Resume (PDF, DOCX)"}</span>
              <input type="file" required accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" />
            </label>
          </div>
        )}

        {/* Submit Button with Loading status */}
        <button type="submit" disabled={saving} className="w-full bg-primary hover:bg-red-700 text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-sm">
          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
          {saving ? 'Processing Request...' : mode === "apply" ? 'Submit Application' : 'Save Profile Changes'}
        </button>
      </form>
    </FormOffCanvas>
  );
};

export default CandidateForm;